import { useShepherdTour } from './useShepherdTour';
import { TourNamesEnum } from '@employ-engage/data';
import { offset } from '@floating-ui/dom';
import type { Tour } from 'shepherd.js';
import { useTranslation } from 'react-i18next';

let setupCompanyTourInstance: Tour | null = null;
let addARoleTourInstance: Tour | null = null;
let createANewTemplateTourInstance: Tour | null = null;
let generateAReportTourInstance: Tour | null = null;

const waitForElementToRender = (selector: string, tour: Tour) => {
  return new Promise<void>((resolve) => {
    const now = Date.now();
    const timer = setInterval(() => {
      if (document.querySelector(selector)) {
        clearInterval(timer);
        resolve();
      }
      if (Date.now() - now > 5000) {
        clearInterval(timer);
        tour.cancel();
        resolve();
      }
    }, 500);
  });
};

export const useSetupCompanyTour = () => {
  const { t } = useTranslation(['common']);
  const { tourInstance: tour } = useShepherdTour({
    tourName: TourNamesEnum.SETUP_COMPANY,
    tourOptions: {
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: {
          enabled: true,
        },
        title: t('Setup a new company'),
        floatingUIOptions: {
          middleware: [offset({ mainAxis: 12, crossAxis: 0 })],
        },
      },
    },
  });

  if (!setupCompanyTourInstance) {
    setupCompanyTourInstance = tour;

    if (tour) {
      tour.addSteps([
        {
          id: 'step-1',
          text: 'Begin with the settings menu',
          modalOverlayOpeningRadius: 16,
          attachTo: {
            element: '.settings-nav-button',
            on: 'right',
          },
          advanceOn: { selector: '.settings-nav-button', event: 'click' },
        },
        {
          id: 'step-2',
          text: 'Click the add button to add a new company level.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.add-org-level-icon',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
          ],
          extraHighlights: ['.company-level-label'],
          advanceOn: { selector: '.add-org-level-icon', event: 'click' },
          beforeShowPromise: () => {
            return waitForElementToRender('.add-org-level-icon', tour);
          },
        },
        {
          id: 'step-3',
          text: 'Type in a new company level.  For example, you could use "Region", "Department", "Team" or "Suborganization".  You can add as many of these as you want after the tour.  This builds your organization hierarchy.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.org-level-name-input',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
            {
              text: 'Next',
              action: tour.next,
            },
          ],
          extraHighlights: ['.org-level-name-input-label'],
          beforeShowPromise: () => {
            return waitForElementToRender('.org-level-name-input', tour);
          },
        },
        {
          id: 'step-4',
          text: 'Click the save button to save the company level.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.save-org-level-button',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
          ],
          advanceOn: { selector: '.save-org-level-button', event: 'click' },
        },
        {
          id: 'step-5',
          text: 'Click the add button to add a new organization within your company.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.add-org-icon-0',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
          ],
          advanceOn: { selector: '.add-org-icon-0', event: 'click' },
        },
        {
          id: 'step-6',
          text: 'Type in a new organization.  For example, you could use "North", "South", "East" or "West".  You can add as many of these as you want after the tour.  This builds your organization hierarchy and at any level.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.org-name-input',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
            {
              text: 'Next',
              action: tour.next,
            },
          ],
          beforeShowPromise: () => {
            return waitForElementToRender('.org-name-input', tour);
          },
        },
        {
          id: 'step-4',
          text: 'Click the save button to save the organization.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.add-org-name-button',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
          ],
          advanceOn: { selector: '.add-org-name-button', event: 'click' },
        },
        {
          id: 'step-final',
          text: 'Tour complete! You can always come back to this page to add more organizations.',
          buttons: [
            {
              text: 'End',
              action: tour.complete,
            },
          ],
          extraHighlights: ['.organization-tree-0'],
        },
      ]);
    }
  }

  return { setupCompanyTourInstance, tourName: TourNamesEnum.SETUP_COMPANY };
};

export const useAddARoleTour = () => {
  const { tourInstance: tour } = useShepherdTour({
    tourName: TourNamesEnum.ADD_A_ROLE,
    tourOptions: {
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: {
          enabled: true,
        },
        title: 'Add a role',
        floatingUIOptions: {
          middleware: [offset({ mainAxis: 12, crossAxis: 0 })],
        },
      },
    },
  });

  if (!addARoleTourInstance) {
    addARoleTourInstance = tour;

    if (tour) {
      tour.addSteps([
        {
          id: 'step-1',
          text: 'Begin with the settings menu',
          modalOverlayOpeningRadius: 16,
          attachTo: {
            element: '.settings-nav-button',
            on: 'right',
          },
          advanceOn: { selector: '.settings-nav-button', event: 'click' },
        },
        {
          id: 'step-2',
          text: 'Type in a new role for cross-organization targeting (this will make more sense later on). You can add as many of these as you want after the tour.',
          modalOverlayOpeningRadius: 6,
          attachTo: {
            element: '.add-role-input',
            on: 'right',
          },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
            {
              text: 'Next',
              action: tour.next,
            },
          ],
          extraHighlights: ['.roles-label'],
          beforeShowPromise: () => {
            return waitForElementToRender('.roles-label', tour);
          },
        },
        {
          id: 'step-3',
          text: "Click the add button to add the role to the list. Be sure to add at least one role before continuing or this button won't work.",
          modalOverlayOpeningRadius: 10,
          attachTo: {
            element: '.add-role-icon-button',
            on: 'right',
          },
          advanceOn: { selector: '.add-role-icon-button', event: 'click' },
          buttons: [
            {
              text: 'Back',
              action: tour.back,
            },
          ],
        },
        {
          id: 'step-final',
          text: 'Tour complete! You can always come back to this page to add more roles.',
          buttons: [
            {
              text: 'End',
              action: tour.complete,
            },
          ],
        },
      ]);
    }
  }

  return { addARoleTourInstance, tourName: TourNamesEnum.ADD_A_ROLE };
};

export const useCreateANewTemplateTour = () => {
  const { tourInstance: tour } = useShepherdTour({
    tourName: TourNamesEnum.CREATE_A_NEW_TEMPLATE,
    tourOptions: {
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: true,
      },
    },
  });

  if (!createANewTemplateTourInstance) {
    createANewTemplateTourInstance = tour;

    if (tour) {
      tour.addSteps([
        {
          id: 'step-1',
          text: 'Begin with the templates page',
          modalOverlayOpeningRadius: 16,
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 12, crossAxis: 0 })],
          },
          attachTo: {
            element: '.templates-nav-button',
            on: 'right',
          },
        },
      ]);
    }
  }

  return {
    createANewTemplateTourInstance,
    tourName: TourNamesEnum.CREATE_A_NEW_TEMPLATE,
  };
};

export const useGenerateAReportTour = () => {
  const { tourInstance: tour, tourName } = useShepherdTour({
    tourName: TourNamesEnum.GENERATE_A_REPORT,
    tourOptions: {
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: true,
      },
    },
  });

  if (!generateAReportTourInstance) {
    generateAReportTourInstance = tour;

    if (tour) {
      tour.addSteps([
        {
          id: 'step-1',
          text: 'Begin with the reports page',
          modalOverlayOpeningRadius: 16,
          floatingUIOptions: {
            middleware: [offset({ mainAxis: 12, crossAxis: 0 })],
          },
          attachTo: {
            element: '.reports-nav-button',
            on: 'right',
          },
        },
      ]);
    }
  }

  return { generateAReportTourInstance: tour, tourName };
};
